import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { normalizeError } from "@shared/lib/errors";

interface RetriableRequestConfig extends InternalAxiosRequestConfig {
  _csrfRetried?: boolean;
}

const axiosClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true,
});

let csrfRefreshPromise: Promise<boolean> | null = null;

axiosClient.interceptors.request.use(
  function (config) {
    // Decode CSRF token from cookies
    const xsrfCookie = document.cookie.split("; ").find((cookie) => cookie.startsWith("XSRF-TOKEN"));

    // Extract token value if the cookie is found
    if (xsrfCookie) {
      const decodedCsrfToken = decodeURIComponent(xsrfCookie.split('=')[1]);
      // Add CSRF token to headers
      config.headers["Accept"] = "application/json";
      config.headers["Content-Type"] = "application/json";
      config.headers["X-XSRF-TOKEN"] = decodedCsrfToken;
    }

    return config;
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error);
  }
);


// Add a response interceptor
axiosClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableRequestConfig | undefined;

    if (originalRequest?._csrfRetried) {
      return Promise.reject(normalizeError(error));
    }

    if (error.response?.status === 419 && originalRequest) {
      if (!csrfRefreshPromise) {
        csrfRefreshPromise = refreshCsrfCookie().finally(() => {
          csrfRefreshPromise = null;
        });
      }

      const sessionRefreshed = await csrfRefreshPromise;

      if (sessionRefreshed) {
        originalRequest._csrfRetried = true;
        return axiosClient.request(originalRequest);
      }
    }

    return Promise.reject(normalizeError(error));
  }
);


// Helper function to refresh CSRF cookie for SPA session auth
const refreshCsrfCookie = async () => {
  try {
    await axiosClient.get("/sanctum/csrf-cookie");
    return true;
  } catch (error) {
    console.error("Failed to refresh csrf cookie:", error);
    return false;
  }
};

export default axiosClient;